import { useState, useEffect } from "react";
import {
  Stack,
  Text,
  Icon,
  IDropdownOption,
  Dropdown,
  DirectionalHint,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
} from "@fluentui/react";
import { styles } from "../noEv2Experiments.styles";
import { useNavigateEv2Experiments } from "../../Hooks/UseNavigateToEv2Experiments";
import { useAppContext } from "../../Contexts";

interface BrowseOtherExtensionsProps {
  extensionName: string | undefined;
}

const BrowseOtherExtensions: React.FC<BrowseOtherExtensionsProps> = ({
  extensionName,
}) => {
  const { extensions, getExtensionMapping, updateExtensionMapping } =
    useAppContext();
  const [selectedExtension, setSelectedExtension] = useState<
    string | undefined
  >(undefined);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [pendingMapping, setPendingMapping] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const { navigateToExperiment: navigateExperiment } =
    useNavigateEv2Experiments();

  // Check for saved mapping on component mount
  useEffect(() => {
    if (extensionName) {
      const mappedExtension = getExtensionMapping(extensionName);

      if (mappedExtension) {
        // Automatically redirect to the mapped extension
        navigateExperiment(mappedExtension);
      }
    }
  }, [extensionName, navigateExperiment, getExtensionMapping]);

  // Create dropdown options from extensions
  const dropdownOptions: IDropdownOption[] =
    extensions?.map((extension) => ({
      key: extension.name,
      text: extension.extensionRepoPath,
      data: extension,
    })) || [];

  const handleExtensionChange = (
    _event: React.FormEvent<HTMLDivElement>,
    option?: IDropdownOption,
  ) => {
    if (option) {
      const selectedExtensionName = option.key as string;
      setSelectedExtension(selectedExtensionName);

      // Show dialog to ask user if they want to save this mapping
      setPendingMapping({
        from: extensionName!,
        to: selectedExtensionName,
      });
      setShowMappingDialog(true);
    }
  };

  const handleSaveMapping = () => {
    if (pendingMapping) {
      updateExtensionMapping(pendingMapping.from, pendingMapping.to);
      navigateExperiment(pendingMapping.to);
    }
    setShowMappingDialog(false);
    setPendingMapping(null);
  };

  const handleSkipMapping = () => {
    if (pendingMapping) {
      navigateExperiment(pendingMapping.to);
    }
    setShowMappingDialog(false);
    setPendingMapping(null);
  };

  const handleCancelMapping = () => {
    setSelectedExtension(undefined);
    setShowMappingDialog(false);
    setPendingMapping(null);
  };

  return (
    <>
      <div className={styles.browseCard}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
          <Stack className={styles.browseContent} tokens={{ childrenGap: 8 }}>
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 8 }}
            >
              <Icon
                iconName="Search"
                className={styles.browseIcon}
                data-testid="browse-icon"
              />
              <Text variant="mediumPlus" className={styles.browseTitle}>
                Browse Other Extensions
              </Text>
            </Stack>
            <Text variant="medium" className={styles.browseSubtitle}>
              If you have already onboarded, select your extension from the list
              below:
            </Text>
          </Stack>
        </Stack>
        <div className={styles.browseDropdownSection}>
          <Dropdown
            placeholder="Select an extension to browse"
            options={dropdownOptions}
            selectedKey={selectedExtension}
            onChange={handleExtensionChange}
            className={styles.browseDropdown}
            calloutProps={{
              directionalHint: DirectionalHint.bottomCenter,
              directionalHintFixed: true,
            }}
          />
        </div>
      </div>

      <Dialog
        maxWidth={500}
        hidden={!showMappingDialog}
        onDismiss={handleCancelMapping}
        dialogContentProps={{
          type: DialogType.close,
          title: "Save Extension Mapping",
          subText: "Would you like to save this mapping?",
        }}
        modalProps={{
          isBlocking: true,
        }}
      >
        <Stack tokens={{ childrenGap: 12 }}>
          <Text>
            Opening <em>{pendingMapping?.from}</em> will automatically redirect
            to <em>{pendingMapping?.to}</em> in the future.
          </Text>
          <Text className={styles.saveExtensionMappingWarning}>
            <Icon
              iconName="Warning"
              className={styles.saveExtensionMappingWarningIcon}
            />
            Note, this action cannot be undone unless you clear your browser
            data.
          </Text>
        </Stack>
        <DialogFooter>
          <PrimaryButton onClick={handleSaveMapping} text="Save" />
          <DefaultButton onClick={handleSkipMapping} text="Don't Save" />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default BrowseOtherExtensions;
